import type { ToolsResponse } from "@/hooks/use-tools"
import { controlApiFetch } from "@/transport/control-api"
import { jsonArray, jsonNumber, jsonObject, jsonString } from "@/transport/decode"
import { expectJson } from "@/transport/http"

function decodeTools(value: unknown): ToolsResponse {
  const result = jsonObject(value, "Server returned invalid tools")
  const machines = jsonArray(result.machines, "Server returned invalid tools")
  for (const value of machines) jsonString(jsonObject(value, "Server returned invalid tools").name)
  const tools = jsonArray(result.tools, "Server returned invalid tools")
  for (const value of tools) {
    const tool = jsonObject(value, "Server returned invalid tools")
    jsonString(tool.id, "Server returned invalid tools")
    jsonString(tool.qualified_id, "Server returned invalid tools")
    jsonObject(tool.manifest, "Server returned invalid tools")
  }
  const webSearch = jsonObject(result.web_search, "Server returned invalid web search configuration")
  jsonString(webSearch.provider, "Server returned invalid web search configuration")
  return { version: jsonNumber(result.version), machines, tools, web_search: webSearch } as ToolsResponse
}

export function getTools(signal?: AbortSignal) {
  return controlApiFetch("/api/tools", { signal }).then((response) => expectJson(response, decodeTools))
}

export function deleteTool(toolID: string) {
  return controlApiFetch(`/api/tools/${encodeURIComponent(toolID)}`, { method: "DELETE" }).then((response) => expectJson(response, (value) => jsonObject(value, "Server returned an invalid tool removal result")))
}

export function copyToolToMachine(toolID: string, machine: string) {
  const path = `/api/tools/${encodeURIComponent(toolID)}/machines/${encodeURIComponent(machine)}`
  return controlApiFetch(path, { method: "POST" }).then((response) => expectJson(response, (value) => jsonObject(value, "Server returned an invalid tool setup result")))
}

export type RemoteToolSettings = { client_id?: string; client_secret?: string; headers?: Record<string, string>; roots?: string[] }

export function updateRemoteToolSettings(toolID: string, machine: string, settings: RemoteToolSettings | null, signal?: AbortSignal) {
  const path = `/api/tools/${encodeURIComponent(toolID)}/machines/${encodeURIComponent(machine)}/connection`
  return controlApiFetch(path, { method: settings === null ? "DELETE" : "PUT", signal, headers: { "Content-Type": "application/json" }, body: settings === null ? undefined : JSON.stringify(settings) }).then(response => expectJson(response, value => jsonObject(value, "Invalid remote settings result")))
}

export type ToolLogin = { tool: string; state: "idle" | "starting" | "waiting" | "complete" | "failed"; message?: string; url?: string; code?: string; expires_at?: number }

function decodeToolLogin(value: unknown): ToolLogin {
  const result = jsonObject(value, "Server returned an invalid tool authentication result")
  const state = jsonString(result.state) as ToolLogin["state"]
  if (!["idle", "starting", "waiting", "complete", "failed"].includes(state)) throw new Error("Server returned an invalid tool authentication state")
  return { tool: jsonString(result.tool), state, message: typeof result.message === "string" ? result.message : undefined, url: typeof result.url === "string" ? result.url : undefined, code: typeof result.code === "string" ? result.code : undefined, expires_at: typeof result.expires_at === "number" ? result.expires_at : undefined }
}

export function removeToolFromMachine(toolID: string, machine: string) {
  const path = `/api/tools/${encodeURIComponent(toolID)}/machines/${encodeURIComponent(machine)}`
  return controlApiFetch(path, { method: "DELETE" }).then((response) => expectJson(response, (value) => jsonObject(value, "Server returned an invalid tool removal result")))
}

export function retryTool(toolID: string, machine: string) {
  const path = `/api/tools/${encodeURIComponent(toolID)}/machines/${encodeURIComponent(machine)}/retry`
  return controlApiFetch(path, { method: "POST" }).then((response) => expectJson(response, (value) => jsonObject(value, "Server returned an invalid tool retry result")))
}

export function authenticateTool(toolID: string, machine: string, signal?: AbortSignal) {
  const path = `/api/tools/${encodeURIComponent(toolID)}/machines/${encodeURIComponent(machine)}/auth`
  return controlApiFetch(path, { method: "POST", signal }).then((response) => expectJson(response, decodeToolLogin))
}

export function completeToolAuthentication(toolID: string, machine: string, callback: { state: string; code: string }, signal?: AbortSignal) {
  const path = `/api/tools/${encodeURIComponent(toolID)}/machines/${encodeURIComponent(machine)}/oauth-callback`
  return controlApiFetch(path, { method: "POST", signal, headers: { "Content-Type": "application/json" }, body: JSON.stringify(callback) }).then((response) => expectJson(response, (value) => jsonObject(value, "Invalid OAuth callback result")))
}

export function getToolAuthentication(toolID: string, machine: string, signal?: AbortSignal) {
  const path = `/api/tools/${encodeURIComponent(toolID)}/machines/${encodeURIComponent(machine)}/auth`
  return controlApiFetch(path, { signal }).then((response) => expectJson(response, decodeToolLogin))
}
